// ─── Optionale Verbose-Logs ─────────────────────────────────────────────────
// window.DS_CART_DEBUG = true  in der Konsole aktivieren für ausführliche Logs.
const _dbg = (...args) => {
  if (window.DS_CART_DEBUG) console.log('[DS Cart Drawer]', ...args);
};
// ────────────────────────────────────────────────────────────────────────────

class DSCartDrawer extends HTMLElement {
  constructor() {
    super();

    this._lacProcessing = false;
    this._isOpen = false;
    // Deferred close: wenn close() während LAC-Verarbeitung aufgerufen wird,
    // wird der Schließvorgang nach queue-end ausgeführt, damit kein
    // request-end den Drawer direkt danach wieder öffnet.
    this._pendingClose = false;

    // ── MutationObserver: erkennt jede externe Entfernung der active-Klasse ─
    // Läuft immer (kein Debug-Flag nötig), da wir den Auslöser finden müssen.
    this._classObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'class') continue;
        const hadActive  = m.oldValue?.split(' ').includes('active') ?? false;
        const hasActive  = this.classList.contains('active');
        if (hadActive && !hasActive && !this._closedByUs) {
          console.group('%c[DS Cart Drawer] ⚠️ active-Klasse extern entfernt!', 'color:red;font-weight:bold');
          console.log('_isOpen:', this._isOpen, '| _lacProcessing:', this._lacProcessing);
          console.trace('Externer Auslöser (call stack)');
          console.groupEnd();
        }
        this._closedByUs = false;
      }
    });
    // Zeichnet den alten Wert auf, damit wir "hatte active" prüfen können.
    this._classObserver.observe(this, { attributes: true, attributeOldValue: true });

    // ── LAC Queue-Tracking ──────────────────────────────────────────────────
    document.addEventListener('liquid-ajax-cart:queue-start', () => {
      this._lacProcessing = true;
      _dbg('queue-start → Schließen gesperrt');
    });

    document.addEventListener('liquid-ajax-cart:queue-end', () => {
      this._lacProcessing = false;
      _dbg('queue-end → Schließen freigegeben', { pendingClose: this._pendingClose });
      if (this._pendingClose) {
        _dbg('queue-end → pendingClose ausführen');
        this.close();
      }
    });

    // ── Request-End: ggf. Drawer öffnen ────────────────────────────────────
    document.addEventListener('liquid-ajax-cart:request-end', (event) => {
      const { requestState, cart } = event.detail;
      const isAdd = requestState.requestType === 'add' && requestState.responseData?.ok;

      _dbg(
        `request-end typ=${requestState.requestType} ok=${requestState.responseData?.ok}`,
        `→ isOpen=${this._isOpen} pendingClose=${this._pendingClose}`
      );

      if (isAdd && !this._isOpen && !this._pendingClose) {
        _dbg('request-end → open() (add, Drawer war geschlossen)');
        this.open();
      }

      this.updateCartEmptyState(cart);
    });

    // ── Overlay-Klick: close() aufrufen (close() übernimmt den LAC-Guard) ──
    this.addEventListener('click', (event) => {
      if (!event.target.matches('#CartDrawer-Overlay')) return;
      _dbg(`Overlay-Klick → close() ${this._lacProcessing ? 'zurückgestellt' : 'ausgeführt'}`);
      this.close();
    });

    // ── Escape-Taste ────────────────────────────────────────────────────────
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (evt.target.closest('input, textarea, select, [contenteditable]')) {
        _dbg('Escape ignoriert – Fokus in Formular-Element');
        return;
      }
      _dbg(`Escape → close() ${this._lacProcessing ? 'zurückgestellt' : 'ausgeführt'}`);
      this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  updateCartEmptyState(cart) {
    const isEmpty = !cart || cart.item_count === 0;
    const emptyWrapper = this.querySelector('.ds-drawer__inner-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');

    // Immer loggen, wenn isEmpty unerwartet true ist (kein Debug-Flag nötig).
    if (isEmpty) {
      console.warn('[DS Cart Drawer] updateCartEmptyState: isEmpty=true', { item_count: cart?.item_count });
    } else {
      _dbg('updateCartEmptyState: isEmpty=false, item_count=', cart?.item_count);
    }

    if (isEmpty) {
      this.classList.add('is-empty');
      if (emptyWrapper) emptyWrapper.style.display = 'block';
      if (cartDrawerItems) {
        cartDrawerItems.classList.add('is-empty');
        cartDrawerItems.style.display = 'none';
      }
    } else {
      this.classList.remove('is-empty');
      if (emptyWrapper) emptyWrapper.style.display = 'none';
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
        cartDrawerItems.style.display = '';
      }
    }
  }

  connectedCallback() {
    if (window.liquidAjaxCart?.cart) {
      this.updateCartEmptyState(window.liquidAjaxCart.cart);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#ds-cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      _dbg('Cart-Icon geklickt → open()');
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this._isOpen || this._pendingClose) {
      _dbg(`open() ignoriert | isOpen=${this._isOpen} pendingClose=${this._pendingClose}`);
      return;
    }
    this._isOpen = true;

    // Immer loggen (kein Debug-Flag) – öffnen ist selten und wichtig.
    console.log('[DS Cart Drawer] open()', { triggeredBy: triggeredBy?.id ?? triggeredBy?.tagName ?? 'intern' });

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Timeout ensures the browser has painted the initial transform state
    // before the CSS transition kicks in.
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent('ds-cart-drawer:opened'));
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this._lacProcessing) {
      this._pendingClose = true;
      // Immer loggen – hilft beim Debuggen des Schließ-Problems.
      console.warn('[DS Cart Drawer] close() zurückgestellt – LAC verarbeitet', {
        isOpen: this._isOpen, pendingClose: this._pendingClose
      });
      return;
    }

    // Immer loggen + Stack-Trace – der entscheidende Auslöser.
    console.group('[DS Cart Drawer] close()');
    console.trace('call stack');
    console.groupEnd();

    this._closedByUs = true;   // MutationObserver: externe Entfernung unterdrücken
    this._pendingClose = false;
    this._isOpen = false;
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') return;
      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;
      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.focus();
    });
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}


class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('ds-cart-drawer', DSCartDrawer);
customElements.define('cart-drawer-items', CartDrawerItems);
